/**
 * Generic change event interface for all form controls Provides consistent event contract across checkbox, radio,
 * select, etc.
 */
export interface ControlChangeEvent<T = any> {
  /** Component instance that emitted the event */
  source: any;
  /** New value after change */
  value: T;
  /** Timestamp when change occurred (milliseconds since epoch) */
  timestamp: number;
}

/**
 * Generic change event class for all form controls Replaces component-specific change event classes (CheckboxChange,
 * RadioChange, etc.) Provides timestamp tracking and consistent event shape
 *
 * @example
 *   ```typescript
 *   // Before: separate classes for each component
 *   export class CheckboxChange {
 *     constructor(public source: HxCheckbox, public checked: boolean) {}
 *   }
 *
 *   // After: generic class for all components
 *   const checkboxChange = new ControlChange<boolean>(this, true);
 *   const selectChange = new ControlChange<string>(this, 'option1');
 *   ```;
 *
 * @typeParam T - Type of value in the change event
 */
export class ControlChange<T = any> implements ControlChangeEvent<T> {
  readonly timestamp = Date.now();

  constructor(
    public source: any,
    public value: T
  ) {}
}

/** Type aliases for common form control value types Makes code more self-documenting */

/** Change event for boolean values (checkbox, toggle, switch) */
export type BooleanChangeEvent = ControlChangeEvent<boolean>;

/** Change event for string values (input, select, textarea) */
export type StringChangeEvent = ControlChangeEvent<string>;

/** Change event for number values (input number, rating) */
export type NumberChangeEvent = ControlChangeEvent<number>;

/** Change event for generic object values (multi-select, custom controls) */
export type ObjectChangeEvent<T> = ControlChangeEvent<T>;

/**
 * Helper to create strongly-typed change events
 *
 * @example
 *   ```typescript
 *   const change = createControlChange(this, 'selected-value');
 *   this.change.emit(change);
 *   ```;
 */
export function createControlChange<T = any>(source: any, value: T): ControlChange<T> {
  return new ControlChange(source, value);
}
